import { useCallback, useEffect, useRef, useState } from "react";

/** Keep a dragging thumb under the pointer while live playback continues. */
export function ReadingPosition({ position, length, onSeek, onSpeedStep }: {
  position: number;
  length: number;
  onSeek: (position: number) => void;
  onSpeedStep: (direction: 1 | -1) => void;
}) {
  const max = Math.max(0, length - 1);
  const [draft, setDraft] = useState<number | null>(null);
  const dragging = useRef(false);
  const pending = useRef<number | null>(null);
  const frame = useRef<number | null>(null);
  const seek = useRef(onSeek);
  seek.current = onSeek;
  const flush = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    const value = pending.current;
    pending.current = null;
    if (value !== null) seek.current(value);
  }, []);
  const finish = () => {
    flush();
    dragging.current = false;
    setDraft(null);
  };
  useEffect(() => () => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
  }, []);
  const value = Math.max(0, Math.min(max, draft ?? position));
  const percent = max ? Math.round(value / max * 100) : 0;
  return <div className="reading-transport">
    <label htmlFor="reading-position">
      <span>Reading position</span>
      <span className="reading-transport-value" data-help="Current word, total words and percentage through the script. Drag the position bar below to move every display." tabIndex={0}>{length ? Math.min(length, Math.floor(value) + 1) : 0} / {length} words <strong>{percent}%</strong></span>
    </label>
    <input id="reading-position" aria-label="Reading position" data-help={length < 2 ? "Add at least two words to move through the script." : "Drag to move every display through the script. Left and Right move one word; Up and Down change the pace."} aria-describedby="reading-position-help"
      aria-valuetext={length ? `Word ${Math.floor(value) + 1} of ${length}, ${percent} percent` : "Empty script"}
      type="range" min={0} max={max} step={0.01} value={value} disabled={length < 2}
      onPointerDown={(event) => {
        dragging.current = true;
        setDraft(Number(event.currentTarget.value));
        // The native range owns its drag capture. Explicit capture on the host
        // prevents WebKit's internal thumb from continuing a mouse drag.
      }}
      onPointerUp={finish} onPointerCancel={finish} onLostPointerCapture={finish} onBlur={finish}
      onChange={(event) => {
        const next = Number(event.currentTarget.value);
        if (!dragging.current) { seek.current(next); return; }
        setDraft(next);
        pending.current = next;
        if (frame.current === null) frame.current = requestAnimationFrame(flush);
      }}
      onKeyDown={(event) => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key === "ArrowUp" || event.key === "ArrowDown") {
          event.preventDefault();
          onSpeedStep(event.key === "ArrowUp" ? 1 : -1);
        } else if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
          event.preventDefault();
          const next = event.key === "Home" ? 0 : event.key === "End" ? max : Math.round(value) + (event.key === "ArrowRight" ? 1 : -1);
          seek.current(Math.max(0, Math.min(max, next)));
        }
      }} />
    <span id="reading-position-help" className="visually-hidden">Drag to move through the script. Left and right move one word. Up and down change reading speed.</span>
  </div>;
}
